/**
 * Cloudflare Turnstile (FR-REG-11). The API script is loaded once per page (explicit render);
 * each embed renders its own widget in `execute` mode, so the challenge runs on submit and the
 * visitor only sees the widget when Cloudflare needs an interaction.
 */

export interface TurnstileApi {
  render(
    el: HTMLElement,
    opts: {
      sitekey: string;
      execution?: 'render' | 'execute';
      appearance?: 'always' | 'execute' | 'interaction-only';
      size?: 'normal' | 'flexible' | 'compact';
      theme?: 'light' | 'dark' | 'auto';
      callback?: (token: string) => void;
      'error-callback'?: (code?: string) => void;
      'expired-callback'?: () => void;
      'timeout-callback'?: () => void;
    },
  ): string;
  execute(id: string): void;
  reset(id: string): void;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __ffsTurnstileLoad?: Promise<TurnstileApi>;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (window.__ffsTurnstileLoad) return window.__ffsTurnstileLoad;
  window.__ffsTurnstileLoad = new Promise<TurnstileApi>((resolve, reject) => {
    const settle = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile unavailable'));
    };
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src^="https://challenges.cloudflare.com/turnstile/"]',
    );
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', settle);
    script.addEventListener('error', () => reject(new Error('Turnstile failed to load')));
    if (!existing) {
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    } else if (window.turnstile) {
      settle();
    }
    // Belt and braces: resolve as soon as the global appears (e.g. a host page that loaded it
    // earlier with an onload callback that already fired).
    let tries = 0;
    const poll = setInterval(() => {
      if (window.turnstile) {
        clearInterval(poll);
        resolve(window.turnstile);
      } else if (++tries > 300) clearInterval(poll);
    }, 100);
  });
  return window.__ffsTurnstileLoad;
}

export interface Widget {
  /** Resolves with a fresh single-use token (runs the challenge if needed). */
  token(): Promise<string>;
  /** Discard the current token so the next `token()` runs a new challenge. */
  reset(): void;
  remove(): void;
}

const TOKEN_TIMEOUT_MS = 45_000;

export function createWidget(ts: TurnstileApi, el: HTMLElement, sitekey: string): Widget {
  let token: string | null = null;
  let waiters: { resolve(t: string): void; reject(e: Error): void }[] = [];
  const flush = (t: string | null, err?: Error) => {
    const list = waiters;
    waiters = [];
    for (const w of list) {
      if (t) w.resolve(t);
      else w.reject(err ?? new Error('Verification failed'));
    }
  };
  const id = ts.render(el, {
    sitekey,
    execution: 'execute',
    appearance: 'interaction-only',
    size: 'flexible',
    callback: (t) => {
      token = t;
      flush(t);
    },
    'error-callback': () => {
      token = null;
      flush(null, new Error('Verification failed'));
    },
    'expired-callback': () => {
      token = null;
    },
    'timeout-callback': () => {
      token = null;
      flush(null, new Error('Verification timed out'));
    },
  });
  return {
    token() {
      if (token) {
        const t = token;
        token = null; // single use
        return Promise.resolve(t);
      }
      return new Promise<string>((resolve, reject) => {
        const timer = setTimeout(() => {
          waiters = waiters.filter((w) => w.resolve !== ok);
          reject(new Error('Verification timed out'));
        }, TOKEN_TIMEOUT_MS);
        const ok = (t: string) => {
          clearTimeout(timer);
          token = null;
          resolve(t);
        };
        waiters.push({
          resolve: ok,
          reject: (e) => {
            clearTimeout(timer);
            reject(e);
          },
        });
        if (waiters.length === 1) {
          try {
            // Tokens are single use: reset before executing so a solved widget runs again.
            ts.reset(id);
            ts.execute(id);
          } catch (e) {
            flush(null, e instanceof Error ? e : new Error('Verification failed'));
          }
        }
      });
    },
    reset() {
      token = null;
      try {
        ts.reset(id);
      } catch {}
    },
    remove() {
      try {
        ts.remove(id);
      } catch {}
    },
  };
}
